import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  Modal,
} from "@mui/material";
import image from "./assets/image.svg";
import CalendarModal from "../../../../../components/CalendarModal/CalendarModal";
const RecoverLostForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    contact: "+91",
    query: "",
  });

  // Form validation state
  const [validation, setValidation] = useState({
    email: { valid: true, message: "" },
    contact: { valid: true, message: "" },
  });

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Google Script URL - replace with your deployed web app URL
  const SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbzVlrahnbfz8tvKFhH0w-yx2rUwoqxZMRbX7c8qmNGrMc7TvSueQDGVLY4WCObp7XY9/exec";

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|mil|co|in|io|ai|tech|info|biz|[a-zA-Z]{2,})$/;
    return emailRegex.test(email);
  };

  // Validate phone number format
  const validateContact = (contact) => {
    // Allow +, spaces, and numbers. Minimum 8 digits, maximum 15 digits.
    const phoneRegex = /^\+?[0-9\s]{8,15}$/;
    return phoneRegex.test(contact);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear validation errors when typing
    if (validation[name] && !validation[name].valid) {
      setValidation((prev) => ({
        ...prev,
        [name]: { valid: true, message: "" },
      }));
    }
  };

  // Validate form fields
  const validateForm = () => {
    let isValid = true;
    const newValidation = { ...validation };

    // Validate email
    if (!formData.email.trim()) {
      newValidation.email = { valid: false, message: "Email is required" };
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newValidation.email = {
        valid: false,
        message: "Please enter a valid email address",
      };
      isValid = false;
    } else {
      newValidation.email = { valid: true, message: "" };
    }

    // Validate contact (only if provided - not required)
    if (formData.contact && formData.contact !== "+91") {
      if (!validateContact(formData.contact)) {
        newValidation.contact = {
          valid: false,
          message: "Please enter a valid phone number",
        };
        isValid = false;
      } else {
        newValidation.contact = { valid: true, message: "" };
      }
    }

    setValidation(newValidation);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Use a direct form submission method
    const form = e.target;
    const formDataObj = new FormData(form);

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors", // This is important for cross-origin requests to Google
      body: formDataObj,
    })
      .then(() => {
        // Reset form
        setFormData({
          email: "",
          contact: "+91",
          query: "",
        });

        setSnackbar({
          open: true,
          message: "Thank you for your query! We'll get back to you soon.",
          severity: "success",
        });
      })
      .catch((error) => {
        console.error("Error!", error.message);

        setSnackbar({
          open: true,
          message: "Something went wrong. Please try again.",
          severity: "error",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f9f7f5",
        color: "#333333",
        padding: { xs: "40px 0", sm: "50px 0", md: "60px 0 40px" },
        borderTop: "1px solid #e0e0e0",
        // borderRadius: { xs: "12px", sm: "16px", md: "20px" },
        display: "flex",
        justifyContent: "center",
        width: "100%",
        overflow: "hidden", // Prevent content from exceeding boundaries
      }}
    >
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: "70%" },
          maxWidth: "1200px",
          mx: "auto", // Ensure horizontal centering
          px: { xs: 1, sm: 2, md: 2 }, // Add horizontal padding inside
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            color: "#333333",
            fontWeight: 600,
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
          }}
        >
          Ready to Disrupt Claims?
        </Typography>

        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          justifyContent="center"
          alignItems="stretch"
          sx={{ width: "100%", mx: 0 }}
        >
          <Grid item xs={12} md={7} sx={{ mb: { xs: 3, md: 4 } }}>
            <Card
              sx={{
                background: "#ffffff",
                color: "#333333",
                borderRadius: { xs: "12px", sm: "16px", md: "20px" },
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                flexDirection: "column",
                height: "100%",
                border: "1px solid #e0e0e0",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "20px", sm: "22px", md: "24px" },
                    mb: { xs: 1.5, sm: 2 },
                    color: "#333333",
                  }}
                >
                  Send Query
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Box mb={{ xs: 1.5, sm: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#333333",
                        mb: 0.75,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      Email*
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      variant="outlined"
                      placeholder="you@company.com"
                      size={isMobile ? "small" : "medium"}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      error={!validation.email.valid}
                      helperText={validation.email.message}
                      FormHelperTextProps={{
                        sx: { color: "#d32f2f !important" },
                      }}
                      InputProps={{
                        sx: {
                          color: "#333333",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43 !important",
                          },
                          "&.Mui-focused": {
                            color: "#333333",
                          },
                          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#d32f2f !important",
                          },
                        },
                        notchedOutline: {
                          borderColor: "#cccccc",
                        },
                      }}
                    />
                  </Box>
                  <Box mb={{ xs: 1.5, sm: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#333333",
                        mb: 0.75,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      Contact
                    </Typography>
                    <TextField
                      fullWidth
                      name="contact"
                      variant="outlined"
                      value={formData.contact}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                      error={!validation.contact.valid}
                      helperText={validation.contact.message}
                      FormHelperTextProps={{
                        sx: { color: "#d32f2f !important" },
                      }}
                      InputProps={{
                        sx: {
                          color: "#333333",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43 !important",
                          },
                          "&.Mui-focused": {
                            color: "#333333",
                          },
                          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#d32f2f !important",
                          },
                        },
                        notchedOutline: {
                          borderColor: "#cccccc",
                        },
                      }}
                    />
                  </Box>
                  <Box mb={{ xs: 1.5, sm: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#333333",
                        mb: 0.75,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      How can we help you?
                    </Typography>

                    <TextField
                      fullWidth
                      name="query"
                      variant="outlined"
                      placeholder="Type here..."
                      multiline
                      minRows={isMobile ? 3 : 4}
                      value={formData.query}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        sx: {
                          color: "#333333",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43 !important",
                          },
                          "&.Mui-focused": {
                            color: "#333333",
                          },
                        },
                        notchedOutline: {
                          borderColor: "#cccccc",
                        },
                      }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-start">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{
                        mt: { xs: 1, sm: 2 },
                        background: "#e95f43",
                        color: "#fff",
                        borderRadius: "20px",
                        px: { xs: 2, sm: 3 },
                        py: { xs: 0.75, sm: 1 },
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        textTransform: "none",
                        "&:hover": {
                          background: "#d54e36",
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CircularProgress
                            size={16}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          Sending...
                        </Box>
                      ) : (
                        "Send Query"
                      )}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            sx={{ display: "flex", mb: { xs: 3, md: 4 } }}
          >
            <Card
              sx={{
                background: "#ffffff",
                color: "#333333",
                borderRadius: { xs: "12px", sm: "16px", md: "20px" },
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                flexDirection: "column",
                height: "100%",
                border: "1px solid #e0e0e0",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CardContent
                sx={{
                  flexGrow: 1,
                  p: { xs: 1.5, sm: 2 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "20px", sm: "22px", md: "24px" },
                    mb: { xs: 1.5, sm: 2 },
                    color: "#333333",
                  }}
                >
                  Talk to an AI expert
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666666",
                    fontWeight: 500,
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.125rem" },
                    mb: 2,
                  }}
                >
                  Get a free 15-minute consultation with our Automation experts.
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    my: { xs: 1, sm: 2 },
                    minHeight: { xs: "120px", sm: "150px", md: "180px" },
                  }}
                >
                  <img
                    src={image}
                    alt="AI Expert"
                    style={{
                      width: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "5px",
                    }}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  sx={{ mt: "auto" }}
                >
                  <Button
                    variant="contained"
                    onClick={handleOpenModal}
                    sx={{
                      mt: { xs: 1, sm: 2 },
                      background: "#e95f43",
                      color: "#fff",
                      borderRadius: "20px",
                      px: { xs: 2, sm: 3 },
                      py: { xs: 0.75, sm: 1 },
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      textTransform: "none",
                      "&:hover": {
                        background: "#d54e36",
                      },
                    }}
                  >
                    Start Pilot
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <CalendarModal open={modalOpen} handleClose={handleCloseModal} />

      <style jsx global>{`
        @media (max-width: 600px) {
          .hide-on-mobile {
            display: none;
          }
        }
      `}</style>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecoverLostForm;
